/**
 * The Submissions tab: what has been uploaded, and getting it back out.
 *
 * Extracted from ProjectDetailPage on 2026-08-25.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { sessionExpiresAt, sessionLengthHours } from "../auth/AuthContext";
import SubmissionWizard from "../wizard/SubmissionWizard";
import FileLink from "../components/FileLink";
import { G, G_DARK, Badge, StatusBadge, Btn, Card, Spinner, Th, cellClip, formatDay,
         formatWhen,
         useSortableColumns, useColumnWidths } from "../components/Shared";
import { SessionLimitNotice } from "../components/Portal";
import { useDisplayTimezone } from "../portalConfig";
import { fmt, StorageCell, useVerifier, useRemover, RemoveRecordDialog, VERIFY_ORDER,
         FILE_ACCESSORS } from "./projectRecords";

function SubmissionRow({ s, canManage, notifying, onNotify, verifyBusy, onVerify, onRemove }) {
  const zone = useDisplayTimezone();
  const [expanded, setExpanded] = useState(false);
  const files = s.uploaded_keys || [];
  const colSpan = 8 + (canManage ? 1 : 0);
  const { sorted: sortedFiles, sortKey: fileSortKey, sortDir: fileSortDir, toggleSort: toggleFileSort } = useSortableColumns(files, FILE_ACCESSORS);
  const { widths: fileWidths, setWidth: setFileWidth, totalWidth: fileTotalWidth } = useColumnWidths({ name: 260, size: 120, md5: 280, download: 110 });
  return (
    <>
      <tr
        style={{ borderBottom: expanded ? "none" : "1px solid #f3f4f6", cursor: files.length ? "pointer" : "default" }}
      >
        <td data-label="Job ID" style={{ padding: "12px 16px" }} onClick={() => files.length && setExpanded(x => !x)}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: G_DARK }}>{s.job_id}</span>
        </td>
        <td data-label="Date" style={{ padding: "12px 16px", fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }} onClick={() => files.length && setExpanded(x => !x)}>
          {formatWhen(s.created_at, { zone })}
        </td>
        <td data-label="Files" style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }} onClick={() => files.length && setExpanded(x => !x)}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {files.length}
            {files.length > 0 && (
              <span style={{ fontSize: 10, color: "#6b7280" }}>{expanded ? "▲" : "▼"}</span>
            )}
          </span>
        </td>
        <td data-label="S3 Destination" style={{ padding: "12px 16px" }} onClick={() => files.length && setExpanded(x => !x)}>
          <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: "#6b7280" }}>
            {s.s3_endpoint_url
              ? <><span style={{ fontSize: 10, color: "#6b7280" }}>{s.s3_endpoint_url.replace(/^https?:\/\//, "")} · </span></>
              : null}
            s3://{s.s3_bucket}/{s.s3_prefix || ""}
          </span>
        </td>
        <td data-label="MD5" style={{ padding: "12px 16px" }} onClick={() => files.length && setExpanded(x => !x)}>
          {s.md5_key
            ? <Badge color="green">md5.sum ✓</Badge>
            : <span style={{ color: "#6b7280", fontSize: 12 }}>—</span>}
        </td>
        <td data-label="Status" style={{ padding: "12px 16px" }} onClick={() => files.length && setExpanded(x => !x)}>
          <StatusBadge status={s.status} />
        </td>
        <td data-label="Storage" style={{ padding: "12px 16px" }} onClick={e => e.stopPropagation()}>
          <StorageCell row={s} canManage={canManage} busy={verifyBusy} onVerify={onVerify} />
        </td>
        <td data-label="Notification" style={{ padding: "12px 16px" }} onClick={() => files.length && setExpanded(x => !x)}>
          {s.notified
            ? <span style={{ fontSize: 12, color: "#6b7280" }}>Notified {s.notified_at ? formatDay(s.notified_at, zone) : ""}</span>
            : s.notify_error
              ? <div style={{ fontSize: 11, color: "#dc2626" }} title={s.notify_error}>
                  Failed: {s.notify_error.slice(0, 50)}{s.notify_error.length > 50 ? "…" : ""}
                </div>
              : <span style={{ fontSize: 12, color: "#6b7280" }}>Not sent</span>}
        </td>
        {canManage && (
          <td data-label="" style={{ padding: "12px 16px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
              {!s.notified && (
                <Btn
                  variant="ghost"
                  disabled={notifying}
                  onClick={() => onNotify(s)}
                  style={{ fontSize: 12, padding: "5px 12px" }}
                >
                  {notifying ? <><Spinner size={10} /> Sending…</> : s.notify_error ? "↻ Retry" : "🔔 Notify"}
                </Btn>
              )}
              {s.notified && (
                <span style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: G }}>✓</span> Notified
                </span>
              )}
              {/* Offered on every row, not just ones already showing as
                  missing: verified_state is only as fresh as the last Check,
                  and hiding the control on a stale "ok" would hide it in
                  exactly the case someone is trying to clean up. Whether it is
                  allowed is decided by the server, against storage, and comes
                  back as blockers in the dialog. */}
              <Btn variant="ghost" onClick={() => onRemove(s)}
                   style={{ fontSize: 12, padding: "5px 10px", color: "#b91c1c" }}>
                Remove…
              </Btn>
            </div>
          </td>
        )}
      </tr>
      {expanded && files.length > 0 && (
        <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
          <td colSpan={colSpan} style={{ padding: "0 16px 12px 40px" }}>
            <div style={{ background: "#f9fafb", borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
              {/* The submitted data, back out. Streamed from the objects as they
                  were submitted — no archive is stored, so this costs nothing
                  until somebody asks. Available to anyone who can see the
                  project, not only an operator: the researcher who sent it is
                  the likeliest person to want it back. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                            borderBottom: "1px solid #e5e7eb", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {files.length} file{files.length === 1 ? "" : "s"}
                </span>
                <span style={{ flex: 1 }} />
                <FileLink path={`/api/projects/${s.project_id}/submissions/${s.id}/archive`}
                          mode="save">Download all (.tar.gz)</FileLink>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: fileTotalWidth }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <Th label="File" style={{ padding: "6px 12px", fontSize: 10, color: "#6b7280" }} sortKey="name" activeKey={fileSortKey} dir={fileSortDir} onSort={toggleFileSort} width={fileWidths.name} onWidth={w => setFileWidth("name", w)} />
                      <Th label="Size" style={{ padding: "6px 12px", fontSize: 10, color: "#6b7280" }} sortKey="size" activeKey={fileSortKey} dir={fileSortDir} onSort={toggleFileSort} width={fileWidths.size} onWidth={w => setFileWidth("size", w)} />
                      <Th label="MD5" style={{ padding: "6px 12px", fontSize: 10, color: "#6b7280" }} sortKey="md5" activeKey={fileSortKey} dir={fileSortDir} onSort={toggleFileSort} width={fileWidths.md5} onWidth={w => setFileWidth("md5", w)} />
                      <Th label="" style={{ padding: "6px 12px", fontSize: 10, color: "#6b7280" }} width={fileWidths.download} onWidth={w => setFileWidth("download", w)} />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFiles.map((f, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                        {/* Submitted filenames are long and unbroken — the one
                            in JOB-529B91 was 49 characters with no spaces to
                            wrap at, so it ran straight over the Size column. */}
                        <td data-label="File" style={{ padding: "7px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#374151", ...cellClip }}
                            title={typeof f === "string" ? f.split("/").pop() : f.name}>
                          {typeof f === "string" ? f.split("/").pop() : f.name}
                        </td>
                        <td data-label="Size" style={{ padding: "7px 12px", fontSize: 12, color: "#6b7280" }}>
                          {typeof f === "object" && f.size ? fmt(f.size) : "—"}
                        </td>
                        <td data-label="MD5" style={{ padding: "7px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#6b7280" }}>
                          {typeof f === "object" && f.md5 ? f.md5 : "—"}
                        </td>
                        <td data-label="" style={{ padding: "7px 12px", textAlign: "right" }}>
                          {/* Older rows recorded a bare key string rather than
                              an object; those have no key to ask for. */}
                          {typeof f === "object" && f.key && (
                            <FileLink mode="save"
                                      path={`/api/projects/${s.project_id}/submissions/${s.id}`
                                            + `/download?key=${encodeURIComponent(f.key)}`}>
                              Download
                            </FileLink>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const SUBMISSION_ACCESSORS = {
  job_id: s => s.job_id,
  date:   s => new Date(s.created_at).getTime(),
  files:  s => (s.uploaded_keys || []).length,
  s3:     s => `${s.s3_bucket || ""}/${s.s3_prefix || ""}`,
  md5:    s => s.md5_key ? 1 : 0,
  status: s => s.status,
  // Worst first, so sorting brings the rows that need attention to the top.
  storage: s => VERIFY_ORDER.indexOf(s.verified_state ?? null),
  notification: s => s.notified ? 2 : s.notify_error ? 0 : 1,
};


export default function SubmissionsTab({ project, canManage, onProjectChanged, sessionHours }) {
  const zone = useDisplayTimezone();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showWizard, setShowWizard]   = useState(false);
  const [error, setError]             = useState(null);
  const [notifying, setNotifying]     = useState({});
  const { sorted: sortedSubmissions, sortKey: subSortKey, sortDir: subSortDir, toggleSort: toggleSubSort } = useSortableColumns(submissions, SUBMISSION_ACCESSORS);
  const { widths: subWidths, setWidth: setSubWidth, totalWidth: subTotalWidth } = useColumnWidths({
    job_id: 140, date: 170, files: 90, s3: 280, md5: 110, status: 110, storage: 190, notification: 190, actions: 220,
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/projects/${project.id}/submissions`)
      .then(data => { setSubmissions(data); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(load, [load]);

  const [verifyBusy, handleVerify] = useVerifier(
    s => `/api/projects/${project.id}/submissions/${s.id}/verify`, load, setError);

  const remove = useRemover(
    s => `/api/projects/${project.id}/submissions/${s.id}`, load, setError, onProjectChanged);

  const handleNotify = async submission => {
    setNotifying(n => ({ ...n, [submission.id]: true }));
    try {
      await api.post(`/api/projects/${project.id}/submissions/${submission.id}/notify`);
    } catch (err) {
      setError(err.message);
    } finally {
      setNotifying(n => ({ ...n, [submission.id]: false }));
      load();   // refresh even on failure, so a persisted notify_error shows up immediately
    }
  };

  if (showWizard) {
    return (
      <Card>
        <h2 style={{ margin: "0 0 22px", fontSize: 17, fontWeight: 700, paddingBottom: 14, borderBottom: `2px solid ${G}` }}>
          New Submission
        </h2>
        {/* The moment it matters most — this is where someone is about to
            commit to hours of transfer. */}
        <SessionLimitNotice hours={sessionHours} sessionHours={sessionLengthHours()} expiresAt={sessionExpiresAt()} zone={zone} kind="upload" />
        {/* onProjectChanged as well as load(): submission_count lives on the
            project, not on this list, so the tab badge and Overview kept the old
            number until a reload (2026-10-07) — ResultsTab's defect a second time. */}
        <SubmissionWizard project={project} onSuccess={() => { setShowWizard(false); load(); onProjectChanged?.(); }} onCancel={() => setShowWizard(false)}
          sessionExpiresAt={sessionExpiresAt()} />
      </Card>
    );
  }

  return (
    <div>
      <SessionLimitNotice hours={sessionHours} sessionHours={sessionLengthHours()} expiresAt={sessionExpiresAt()} zone={zone} kind="upload" />
      {!project.s3_configured && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", color: "#92400e", fontSize: 13, marginBottom: 16 }}>
          This project has no storage, so nothing can be submitted yet. An admin sets the portal's storage up, and this project takes it then.
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center",
                    marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        {/* The server runs the submit/start gate for every project response and
            says why it refuses — the ResultsTab pattern. Opening the wizard
            anyway put the refusal on the Review step, after the files were
            chosen. */}
        {project.can_transfer_data === false && (
          <span role="status" style={{ fontSize: 12, color: "#92400e", marginRight: "auto" }}>
            {project.transfer_blocked_reason || "This project cannot take data yet."}
          </span>
        )}
        <Btn onClick={() => setShowWizard(true)}
             disabled={!project.s3_configured || project.can_transfer_data === false}>+ New Submission</Btn>
      </div>

      {error && <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#6b7280" }}>Loading…</div>
      ) : submissions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📤</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No submissions yet</div>
          <div style={{ fontSize: 13 }}>Use "New Submission" to upload data files to S3.</div>
        </div>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: subTotalWidth - (canManage ? 0 : subWidths.actions) }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <Th label="Job ID" sortKey="job_id" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.job_id} onWidth={w => setSubWidth("job_id", w)} />
                  <Th label="Date" sortKey="date" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.date} onWidth={w => setSubWidth("date", w)} />
                  <Th label="Files" sortKey="files" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.files} onWidth={w => setSubWidth("files", w)} />
                  <Th label="S3 Destination" sortKey="s3" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.s3} onWidth={w => setSubWidth("s3", w)} />
                  <Th label="MD5" sortKey="md5" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.md5} onWidth={w => setSubWidth("md5", w)} />
                  <Th label="Status" sortKey="status" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.status} onWidth={w => setSubWidth("status", w)} />
                  <Th label="Storage" sortKey="storage" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.storage} onWidth={w => setSubWidth("storage", w)} />
                  <Th label="Notification" sortKey="notification" activeKey={subSortKey} dir={subSortDir} onSort={toggleSubSort} width={subWidths.notification} onWidth={w => setSubWidth("notification", w)} />
                  {canManage && <Th label="" width={subWidths.actions} onWidth={w => setSubWidth("actions", w)} />}
                </tr>
              </thead>
              <tbody>
                {sortedSubmissions.map(s => (
                  <SubmissionRow key={s.id} s={s} canManage={canManage} notifying={!!notifying[s.id]} onNotify={handleNotify}
                    verifyBusy={!!verifyBusy[s.id]} onVerify={handleVerify} onRemove={remove.open} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {remove.info && (
        <RemoveRecordDialog info={remove.info} busy={remove.busy}
                            onConfirm={remove.confirm} onClose={remove.close} />
      )}
    </div>
  );
}

// ── Results tab ───────────────────────────────────────────────────────────────

